import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ListChecks, Pencil, Plus, Repeat } from "lucide-react";
import { Button, IconButton, Typography } from "@akuna/ui";
import { activityTypeIcon } from "../activities/activityTypeIcons";
import { useToggleActivityCompletion } from "../activities/useActivityMutations";
import { useStayActivities } from "../activities/useStayActivities";
import { isActivityVisibleForPet, type StayActivityRecord } from "../activities/activitiesModel";
import NewActivityDialog from "../activities/NewActivityDialog";
import { InlinePetActivityForm } from "./InlinePetActivityForm";
import { SectionBoundary } from "./SectionBoundary";

export interface StayActivitiesSectionProps {
  petId: string;
  day: string;
  /** No check-in as atividades são leitura; no acompanhar dá para confirmar. */
  canComplete: boolean;
}

/** A cor do tipo fica só no ícone: o destaque da linha é horário + descrição. */
const typeIconStyle = (color: string) => ({
  color,
  background: `color-mix(in srgb, ${color} 12%, transparent)`,
});

/**
 * Alimentação e medicação não são widgets próprios: são `stay_activities` com
 * tipo, horário e conclusão. Esta seção é a leitura delas para um pet no dia.
 */
export function StayActivitiesSection({ petId, day, canComplete }: StayActivitiesSectionProps) {
  const { t } = useTranslation("daycareHotel");
  const activities = useStayActivities({ day, show: "all" });
  const toggle = useToggleActivityCompletion();
  const [isAdding, setIsAdding] = useState(false);
  const [editingActivity, setEditingActivity] = useState<StayActivityRecord | null>(null);

  if (activities.isLoading || activities.isError) {
    return <SectionBoundary isLoading={activities.isLoading} isError={activities.isError} skeletonClassName="h-28 rounded-2xl" />;
  }

  // `useStayActivities` devolve o dia inteiro da clínica. A atividade geral
  // pertence apenas à visão geral; no pet entram as próprias e compartilhadas.
  const rows = (activities.data ?? []).filter(
    (row) => isActivityVisibleForPet(row.targetType, row.targetPetId, petId),
  );

  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <ListChecks aria-hidden="true" size={18} className="text-akuna-blue" />
        <Typography variant="h5">{t("panel.sectionActivities")}</Typography>
        {rows.length > 0 ? (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground">
            {rows.length}
          </span>
        ) : null}
        {canComplete && <IconButton
          className="ml-auto"
          variant="ghost"
          size="icon-sm"
          label={t("panel.addActivity")}
          onClick={() => setIsAdding(true)}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
        </IconButton>}
      </div>
      {rows.length === 0 ? (
        <Typography variant="body-sm" className="mb-2 text-muted-foreground">{t("panel.activitiesEmptyNoRecords")}</Typography>
      ) : (
        <ul className="mb-2 flex flex-col gap-2">
          {rows.map((row) => (
            <li
              key={row.id}
              className={`flex items-center gap-3 rounded-2xl border border-border bg-card px-3 py-2.5 shadow-sm ${row.done ? "opacity-70" : ""}`}
            >
              <span
                className="flex size-9 shrink-0 items-center justify-center rounded-xl"
                style={typeIconStyle(row.typeColor)}
                title={row.typeName}
              >
                {(() => { const Icon = activityTypeIcon(row.typeIcon); return <Icon aria-hidden="true" size={18} />; })()}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                  {row.time && (
                    <Typography variant="body" className="font-semibold tabular-nums text-foreground">{row.time}</Typography>
                  )}
                  <Typography variant="caption" className="text-muted-foreground">{row.typeName}</Typography>
                </div>
                {row.description && (
                  <Typography variant="body-sm" className="line-clamp-2 text-foreground">{row.description}</Typography>
                )}
                {row.recurrenceLabel && (
                  <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-akuna-purple">
                    <Repeat aria-hidden="true" size={13} /> {row.recurrenceLabel}
                  </span>
                )}
              </div>
              {row.done ? (
                <span
                  className="flex size-7 shrink-0 items-center justify-center rounded-full border border-state-success/35 bg-state-success-soft"
                  title={t("panel.activityDone")}
                >
                  <Check aria-hidden="true" size={15} className="text-state-success" />
                  <span className="sr-only">{t("panel.activityDone")}</span>
                </span>
              ) : canComplete ? (
                <div className="flex shrink-0 items-center gap-1">
                  <IconButton
                    variant="ghost"
                    size="icon-sm"
                    label={t("activities.editActivity")}
                    onClick={() => setEditingActivity(row.record)}
                  >
                    <Pencil className="h-4 w-4" aria-hidden="true" />
                  </IconButton>
                  <Button
                    size="sm"
                    className="rounded-full px-4"
                    disabled={toggle.isPending}
                    onClick={() => toggle.mutate({ activityId: row.id, day, done: true })}
                  >
                    {t("panel.activityConfirm")}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {canComplete && <InlinePetActivityForm petId={petId} day={day} open={isAdding} onOpenChange={setIsAdding} />}
      {editingActivity && (
        <NewActivityDialog
          open
          onOpenChange={(open) => { if (!open) setEditingActivity(null); }}
          defaultDate={editingActivity.scheduledDate ?? day}
          activity={editingActivity}
        />
      )}
    </section>
  );
}
